/**
 * Dynamic-import loaders shared by React.lazy (lazyPages.tsx) and by code
 * prefetching. Calling a loader early only starts the chunk download; the
 * browser's module cache means the later lazy() call reuses the same request.
 * Loaders only fetch code — never API data.
 */

/** Retry dynamic imports — Vite restarts and the Cursor→agent TCP proxy often drop chunks. */
export function importWithRetry<T>(loader: () => Promise<T>, attempt = 0): Promise<T> {
  return loader().catch((err) => {
    if (attempt >= 2) throw err;
    const delayMs = 400 * (attempt + 1);
    return new Promise<T>((resolve) => setTimeout(resolve, delayMs)).then(() =>
      importWithRetry(loader, attempt + 1),
    );
  });
}

export const routeLoaders = {
  overview: () => import('../pages/OverviewPage'),
  projects: () => import('../components/projects/ProjectsView'),
  monitoring: () => import('../pages/MonitoringPage'),
  settings: () => import('../components/settings/SettingsView'),
  projectHome: () => import('../components/projects/ProjectHomeView'),
  phase0Repository: () => import('../components/projects/phase0/Phase0RepositoryTab'),
  phase1Insights: () => importWithRetry(() => import('../components/projects/phase1/Phase1InsightsTab')),
  phase1Graph: () =>
    importWithRetry(() =>
      import('../components/projects/phase1/Phase1GraphTab').then((m) => ({ default: m.default })),
    ),
  phase1Knowledge: () =>
    importWithRetry(() =>
      import('../components/projects/phase1/Phase1KnowledgeTab').then((m) => ({ default: m.default })),
    ),
  operateLayout: () => import('../components/projects/workspace/OperateLayout'),
  incidents: () => import('../pages/IncidentsDomainPage'),
  runtimeLogs: () => import('../pages/RuntimeLogsDomainPage'),
  reported: () => import('../pages/ReportedIncidentsPage'),
  executions: () => import('../pages/ExecutionsPage'),
  executors: () => import('../pages/ExecutorCenterPage'),
  certification: () => import('../pages/CertificationPage'),
  operateFixes: () => import('../components/projects/operate/OperateFixesTab'),
  operateTestEnvironment: () =>
    importWithRetry(() => import('../components/projects/operate/OperateTestEnvironmentTab')),
  operateLearning: () => import('../components/projects/operate/OperateLearningTab'),
};

type LoaderKey = keyof typeof routeLoaders;

/** Operate tab segment → page chunk (Requests is bundled eagerly). */
const OPERATE_TAB_LOADERS: Record<string, LoaderKey> = {
  '': 'incidents',
  incidents: 'incidents',
  'runtime-logs': 'runtimeLogs',
  reported: 'reported',
  executions: 'executions',
  executors: 'executors',
  replay: 'certification',
  'test-environment': 'operateTestEnvironment',
  engineering: 'operateFixes',
  fixes: 'operateFixes',
  learning: 'operateLearning',
};

/** The Operate page chunk for a pathname, if the path is inside Operate. */
export function operateTabLoader(pathname: string): (() => Promise<unknown>) | null {
  const parts = pathname.split('/');
  const idx = parts.indexOf('operate');
  if (idx < 0) return null;
  const key = OPERATE_TAB_LOADERS[parts[idx + 1] ?? ''];
  return key ? routeLoaders[key] : null;
}

/** Chunks a navigation to `pathname` will need, in the order they are rendered. */
function loadersForPath(pathname: string): Array<() => Promise<unknown>> {
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, '');
  if (path === '/overview' || path === '') return [routeLoaders.overview];
  if (path === '/projects/all' || path === '/projects/active') return [routeLoaders.projects];
  if (path.startsWith('/monitoring')) return [routeLoaders.monitoring];
  if (path.startsWith('/settings')) return [routeLoaders.settings];
  const m = path.match(/^\/projects\/[^/]+(\/.*)?$/);
  if (!m) return [];
  const rest = m[1] ?? '';
  if (rest === '') return [routeLoaders.projectHome];
  if (rest.startsWith('/phase-0/repository')) return [routeLoaders.phase0Repository];
  if (rest.startsWith('/phase-1/insights')) return [routeLoaders.phase1Insights];
  if (rest.startsWith('/phase-1/graph')) return [routeLoaders.phase1Graph];
  if (rest.startsWith('/phase-1/knowledge')) return [routeLoaders.phase1Knowledge];
  if (rest.startsWith('/operate')) {
    // OperateLayout is bundled eagerly (lazyPages.tsx); only prefetch the tab.
    const page = operateTabLoader(rest);
    return page ? [page] : [];
  }
  return [];
}

const prefetched = new Set<() => Promise<unknown>>();

/**
 * Start downloading the code for a navigation target (sidebar hover / focus /
 * press). Each target is fetched at most once per session; failures are
 * ignored here and surface normally when the route actually renders.
 */
export function prefetchRouteCode(pathname: string): void {
  for (const load of loadersForPath(pathname)) {
    if (prefetched.has(load)) continue;
    prefetched.add(load);
    load().catch(() => prefetched.delete(load));
  }
}
